// localStorage state persistence + a couple of shared UI constants.
//
// Tenant/client data comes from the REST API (fetchTenants) — there are NO
// seeded demo clients here on purpose: an empty fallback means a fresh load
// (empty localStorage: first visit, incognito, cleared cache) shows nothing
// until the first fetch resolves, instead of flashing fake demo clients.

import type { AppState } from '../types';

export const LS_KEY = 'shoptefy-master-admin-v1';

// Referenced by the service node-picker comment in Clients.tsx as the old
// hardcoded fallback; the live picker uses /nodes now.
export const NODES = [
  'node-pool-a-1', 'node-pool-a-2', 'node-pool-a-3',
  'node-gpu-1', 'node-gpu-2',
  'node-storage-1', 'node-storage-2',
];

export const PLANS = ['starter', 'growth', 'scale', 'enterprise', 'unlimited'] as const;

export function loadState(): AppState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as AppState;
      if (parsed && parsed.__v === 3) {
        // Backfill settings fields added after this blob was persisted so a
        // returning user never has an undefined field (e.g. managedStorageEnabled,
        // which gates the New bucket action). The live /settings fetch overwrites
        // this shortly after, but this keeps the UI usable until it lands.
        return { ...parsed, settings: { managedStorageEnabled: false, ...parsed.settings } };
      }
    }
  } catch (e) { console.warn('loadState failed', e); }
  // No persisted state yet — start with an empty client list. Real tenants
  // arrive from fetchTenants(); seeding fake clients here made them flash on
  // first load before the fetch replaced them.
  return {
    __v: 3,
    auth: null,
    clients: [],
    settings: {
      baseRepo: 'git@github.com:shoptefy/tenant-base.git',
      baseBranch: 'main',
      argoServer: 'https://argo.shoptefy.io',
      registry: 'registry.shoptefy.io',
      defaultPlan: 'growth',
      // Assume absent until /settings says otherwise, so the action never
      // flashes on a deployment that cannot honour it.
      managedStorageEnabled: false,
    },
  };
}

export function saveState(state: AppState): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch (e) { console.warn('saveState failed', e); }
}
